<script>
import {computed, onMounted, ref} from "vue";
import WCodeGeneration from "@/components/WCodeGeneration.vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useOccupationStore} from "@/stores/Occupation";
import StrCategoryAutocomplete from "@/components/StrCategoryAutocomplete.vue";
import PaySectionSelection from "@/components/PaySectionSelection.vue";
import axios from "axios";

export default {
    components: {
        PaySectionSelection,
        StrCategoryAutocomplete,
        WCodeGeneration,
    },
    props: {
        addAction: Boolean,
        duplicateAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, overlay, loading, backgroundColor} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const occupationStore = useOccupationStore();
        
        
        const occupation = ref({
            code: null,
            paySectionss: props.addAction ? [] : null,
        });
        
        function findById(id) {
            loading.value = true;
            axios.get(`/occupations/${id}`).then((response) => {
                if (props.duplicateAction) {
                    occupation.value.categoryId = response.data.categoryId;
                    occupation.value.socioProfessionalId = response.data.socioProfessionalId;
                    occupation.value.baseSalary = response.data.baseSalary;
                    occupation.value.paySectionss = [];
                    response.data.paySectionss.forEach(op => {
                        occupation.value.paySectionss.push({
                            value: op.value,
                            ordre: op.ordre,
                            paySectionId: op.paySectionId,
                            paySectionCode: op.paySectionCode,
                            paySectionName: op.paySectionName,
                            paySectionFormula: op.paySectionFormula,
                            calculated: op.calculated,
                            result: op.result,
                            byOccupation: op.byOccupation,
                            position: op.position,
                        });
                    });
                } else {
                    occupation.value = response.data;
                }
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/occupations', occupation.value).then((response) => {
                    occupationStore.addOccupation(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/occupations', occupation.value).then((response) => {
                    occupationStore.updateOccupation(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/occupations/${$route.params.id}`).then(() => {
                occupationStore.deleteOccupation(occupation.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function updatePaySection(items) {
            occupation.value.paySectionss = items;
        }
        
        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });
        
        return {
            mxRules,
            dialog,
            overlay,
            loading,
            backgroundColor,
            readonly,
            occupation,
            updatePaySection,
            save,
            update,
            remove,
            getBack
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="900"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction || duplicateAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @cancel="overlay = false"
                @remove="remove"
            ></w-overlay>
            <v-card-text>
                <v-tabs
                    class="tabs"
                    color="teal"
                    :background-color="backgroundColor"
                    vertical
                    slider-size="7"
                >
                    <v-tab>
                        <strong>{{ $t('common.details') }}</strong>
                    </v-tab>
                    <v-tab>
                        <strong>{{ $t('common.pay') }}</strong>
                    </v-tab>
                    
                    <v-tab-item>
                        <v-container fluid>
                            <v-form ref="form" lazy-validation>
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-code-generation
                                            v-model="occupation.code"
                                            :entity="'OCCUPATION'"
                                            :load="addAction || duplicateAction"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.name')" required/>
                                        <w-text-field
                                            v-model="occupation.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label=" $t('data_select.SOCIO_PROFESSIONAL')"/>
                                        <w-data-select-autocomplete
                                            v-model="occupation.socioProfessionalId"
                                            :readonly="readonly"
                                            type="SOCIO_PROFESSIONAL"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.nameInArabic')"/>
                                        <w-text-field
                                            v-model="occupation.nameInArabic"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.description')"/>
                                        <w-simple-text-area
                                            v-model="occupation.description"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-container>
                    </v-tab-item>
                    <v-tab-item>
                        <v-container fluid>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('occupation.category')"/>
                                    <str-category-autocomplete
                                        v-model="occupation.categoryId"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                            <pay-section-selection
                                v-if="occupation.paySectionss"
                                :initial-pay-section-selection="occupation.paySectionss"
                                by-occupation
                                :pay-preview="false"
                                :readonly="readonly"
                                @updatePaySection="updatePaySection"
                            ></pay-section-selection>
                        </v-container>
                    </v-tab-item>
                </v-tabs>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
div.tabs [role="tab"] {
    justify-content: flex-start;
}
</style>